import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { fetchChargerAudit, type AuditRow } from '@/lib/api';
import styles from './ChargerDetailPage.module.css';

interface AuditPanelProps {
  chargerId: string;
}

/**
 * Operator action history for a single charger. Lists every audit
 * entry written by the command surface, the rename endpoint, and any
 * other code path that calls `writeAuditLog` against this resource.
 *
 * Cursor pagination via `before` (newest entry's `created_at`) so
 * the list stays stable while operators take new actions in another
 * tab.
 */
export function AuditPanel({ chargerId }: AuditPanelProps) {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  // Inlined initial load — kept inside the effect so we don't have
  // to declare a callback that captures `rows` (which would need
  // a hooks/exhaustive-deps suppression). The "load older" button
  // uses the separate `loadMore` below.
  useEffect(() => {
    let cancelled = false;
    setRows([]);
    setHasMore(true);
    setLoading(true);
    setError('');
    fetchChargerAudit(chargerId, { limit: 50 })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setHasMore(res.meta.nextBefore !== null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load audit');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chargerId]);

  const loadMore = async () => {
    setLoading(true);
    setError('');
    try {
      const oldest = rows.at(-1)?.created_at;
      const res = await fetchChargerAudit(chargerId, {
        limit: 50,
        ...(oldest ? { before: oldest } : {}),
      });
      setRows((prev) => [...prev, ...res.data]);
      setHasMore(res.meta.nextBefore !== null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchChargerAudit(chargerId, { limit: 50 });
      setRows(res.data);
      setHasMore(res.meta.nextBefore !== null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.auditPanel}>
      <div className={styles.auditHeader}>
        <button
          className={styles.logsCtl}
          onClick={() => void refresh()}
          disabled={loading}
        >
          {loading
            ? <><Loader2 size={13} className={styles.cmdSpinner} /> Loading…</>
            : <><RefreshCw size={13} /> Refresh</>}
        </button>
      </div>

      {error && <div className={styles.logsError}>{error}</div>}

      {rows.length === 0 && !loading && (
        <p className={styles.logsEmpty}>No audited actions for this charger yet.</p>
      )}

      <div className={styles.auditList}>
        {rows.map((row) => (
          <AuditCard
            key={row._id}
            row={row}
            isOpen={openId === row._id}
            onToggle={() => setOpenId(openId === row._id ? null : row._id)}
          />
        ))}
      </div>

      {hasMore && rows.length > 0 && (
        <div className={styles.logsFooter}>
          <button
            className={styles.logsCtl}
            onClick={() => void loadMore()}
            disabled={loading}
          >
            {loading
              ? <><Loader2 size={13} className={styles.cmdSpinner} /> Loading…</>
              : 'Load older'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── AuditCard ────────────────────────────────────────────────────

function AuditCard({
  row, isOpen, onToggle,
}: { row: AuditRow; isOpen: boolean; onToggle: () => void }) {
  const date = new Date(row.created_at);
  return (
    <button type="button" className={styles.auditCard} onClick={onToggle}>
      <span className={styles.auditTime}>{date.toLocaleString(undefined, { hour12: false })}</span>
      <span className={styles.auditAction}>{row.action}</span>
      <span className={styles.auditUser}>
        {row.user_id ? `user ${row.user_id.slice(-6)}` : 'system'}
      </span>
      <span className={styles.auditChev}>
        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </span>
      {isOpen && (
        <div className={styles.auditDetails} onClick={(e) => e.stopPropagation()}>
          {row.payload !== undefined && (
            <>
              <p className={styles.auditDetailLabel}>Payload</p>
              <pre className={styles.auditPayload}>{JSON.stringify(row.payload, null, 2)}</pre>
            </>
          )}
          {row.response !== undefined && (
            <>
              <p className={styles.auditDetailLabel}>Response</p>
              <pre className={styles.auditPayload}>{JSON.stringify(row.response, null, 2)}</pre>
            </>
          )}
          {row.ip_address && (
            <p className={styles.auditMeta}>IP: <code>{row.ip_address}</code></p>
          )}
        </div>
      )}
    </button>
  );
}
